import * as playerRepository from "../repositories/playerRepository";
import {
  patchUserProfile,
  fetchUserProfile,
  deleteUserProfile,
} from "../repositories/userRepository";
import {
  deleteCurrentAuthUser,
  getCurrentAuthUser,
  signOutCurrentUser,
} from "../repositories/authRepository";
import { ensureUserProfile } from "./userService";
import { validateDisplayName } from "../domain/identity/playerNameSearch";
import {
  ACCOUNT_CLOSE_MODE_ERASE,
  ACCOUNT_CLOSE_MODE_RETAIN,
  ACCOUNT_STATUS_ACTIVE,
  ACCOUNT_STATUS_CLOSED_RETAIN,
  isAccountCloseMode,
} from "../domain/identity/accountClose";
import { trackProfileCreated } from "./analytics/bridges/profileAnalytics";
import { trackAccountDeleted } from "./analytics/bridges/authAnalytics";

/** @typedef {import('../domain/identity/playerTypes').PlayerRecord} PlayerRecord */

export { validateDisplayName };

/**
 * @param {{ displayName: string; createdBy: string; userId?: string | null; playerId?: string }} input
 * @returns {Promise<PlayerRecord>}
 */
export async function createPlayer(input) {
  if (input.playerId) {
    return playerRepository.create({
      displayName: input.displayName,
      createdBy: input.createdBy,
      userId: input.userId ?? null,
      playerId: input.playerId,
    });
  }
  return playerRepository.create({
    displayName: input.displayName,
    createdBy: input.createdBy,
    userId: input.userId ?? null,
  });
}

/**
 * @param {string} playerId
 * @returns {Promise<PlayerRecord | null>}
 */
export async function resolvePlayer(playerId) {
  if (!playerId) return null;
  return playerRepository.findById(playerId);
}

/**
 * @param {{ displayName: string; createdBy: string; userId?: string | null }} input
 * @returns {Promise<PlayerRecord>}
 */
export async function findOrCreatePlayer(input) {
  return playerRepository.findOrCreate(input);
}

/**
 * @param {string} playerId
 * @param {string} userId
 * @returns {Promise<PlayerRecord>}
 */
export async function claimPlayer(playerId, userId) {
  const claimed = await playerRepository.claimPlayer(playerId, userId);
  const profile = await fetchUserProfile(userId);
  if (!profile?.playerId) {
    await patchUserProfile(userId, { playerId: claimed.playerId });
  }
  return claimed;
}

/**
 * Attach a named, unclaimed partner to this account when the account has no player yet.
 * An account that already has a player keeps it; the named spot stays as entered.
 * @param {string} userId
 * @param {string} playerId
 */
export async function claimNamedPartner(userId, playerId) {
  if (!userId || !playerId) return { claimed: false, reason: "missing" };
  await ensureUserProfile(userId);
  const profile = await fetchUserProfile(userId);
  if (profile?.playerId && profile.playerId !== playerId) {
    return { claimed: false, reason: "already_linked" };
  }
  if (profile?.playerId === playerId) return { claimed: true };
  await claimPlayer(playerId, userId);
  return { claimed: true };
}

/**
 * @param {string} query
 * @param {number} [limit]
 * @returns {Promise<PlayerRecord[]>}
 */
export async function searchPlayers(query, limit = 25) {
  return playerRepository.search({ query, limit, activeOnly: true });
}

/**
 * @param {string} sourcePlayerId
 * @param {string} targetPlayerId
 */
export async function mergePlayers(sourcePlayerId, targetPlayerId) {
  return playerRepository.merge(sourcePlayerId, targetPlayerId);
}

/**
 * @param {string} playerId
 */
export async function deactivatePlayer(playerId) {
  return playerRepository.deactivate(playerId);
}

/**
 * If the user soft-closed with retain, restore account + player on next sign-in.
 * @param {string} userId
 */
export async function reactivateClosedRetainAccount(userId) {
  if (!userId) return null;
  const profile = await fetchUserProfile(userId);
  if (!profile) return null;

  const needsUser =
    profile.accountStatus === ACCOUNT_STATUS_CLOSED_RETAIN;
  let player = null;
  if (profile.playerId) {
    player = await resolvePlayer(profile.playerId);
  }

  const needsPlayer = Boolean(player && player.userId === userId && player.isActive === false);

  if (needsUser) {
    await patchUserProfile(userId, {
      accountStatus: ACCOUNT_STATUS_ACTIVE,
      accountClosedAtMs: null,
    });
  }
  if (needsPlayer && player) {
    player = await playerRepository.reactivate(player.playerId);
  }

  return { profile: await fetchUserProfile(userId), player };
}

/**
 * Close the signed-in account.
 * - retain: soft-close user + deactivate player; Auth kept; sign out.
 * - erase: anonymize player, delete users doc + Auth; match history kept.
 *
 * @param {{ mode: 'retain' | 'erase'; userId?: string; playerId?: string | null }} input
 * @returns {Promise<{ mode: 'retain' | 'erase' }>}
 */
export async function closeAccount(input) {
  const mode = input?.mode;
  if (!isAccountCloseMode(mode)) {
    throw new Error("Invalid account close mode");
  }

  const authUser = getCurrentAuthUser();
  const userId = input.userId || authUser?.uid;
  if (!userId || !authUser || authUser.uid !== userId) {
    throw new Error("You must be signed in to delete your account.");
  }

  const profile = await fetchUserProfile(userId);
  const playerId = input.playerId ?? profile?.playerId ?? null;

  if (mode === ACCOUNT_CLOSE_MODE_RETAIN) {
    await patchUserProfile(userId, {
      accountStatus: ACCOUNT_STATUS_CLOSED_RETAIN,
      accountClosedAtMs: Date.now(),
    });
    if (playerId) {
      const player = await resolvePlayer(playerId);
      if (player && player.userId === userId && player.isActive !== false) {
        await playerRepository.closeRetain(playerId);
      }
    }
    trackAccountDeleted({ deletionReasonCode: "retain" });
    await signOutCurrentUser();
    return { mode: ACCOUNT_CLOSE_MODE_RETAIN };
  }

  // erase
  if (playerId) {
    const player = await resolvePlayer(playerId);
    if (!player) {
      throw new Error("Player profile not found.");
    }
    if (player.userId !== userId) {
      throw new Error("This player profile is not linked to your account.");
    }
    await playerRepository.closeErase(playerId);
  }

  try {
    await deleteUserProfile(userId);
  } catch (err) {
    // Missing users doc is fine mid-retry.
    const code =
      err && typeof err === "object" && "code" in err ? String(err.code) : "";
    if (code !== "not-found") throw err;
  }

  trackAccountDeleted({ deletionReasonCode: "erase" });
  await deleteCurrentAuthUser(authUser);
  return { mode: ACCOUNT_CLOSE_MODE_ERASE };
}

/** Same-tab calls share one create so a burst of sign-in work cannot write several players. */
const ensurePlayerInflight = new Map();

/**
 * Ensure the signed-in user has a Player row linked from `users/{uid}.playerId`.
 * Does not use name search (Firestore list rules); creates a dedicated player when needed.
 * @param {string} userId
 * @param {string} displayName
 */
export function ensurePlayerForUser(userId, displayName) {
  const running = ensurePlayerInflight.get(userId);
  if (running) return running;
  const job = ensurePlayerForUserOnce(userId, displayName).finally(() => {
    if (ensurePlayerInflight.get(userId) === job) ensurePlayerInflight.delete(userId);
  });
  ensurePlayerInflight.set(userId, job);
  return job;
}

/**
 * @param {string} userId
 * @param {string} displayName
 */
async function ensurePlayerForUserOnce(userId, displayName) {
  await ensureUserProfile(userId);
  await reactivateClosedRetainAccount(userId);
  const profile = await fetchUserProfile(userId);
  if (profile?.playerId) {
    const existing = await resolvePlayer(profile.playerId);
    if (existing) {
      if (!existing.userId) {
        return claimPlayer(existing.playerId, userId);
      }
      if (existing.userId === userId) {
        return existing;
      }
      throw new Error("Your account is linked to a player profile that belongs to another user.");
    }
  }

  const validated = validateDisplayName(displayName || "Player");
  const name = validated.ok ? validated.displayName : "Player";
  const player = await createPlayer({
    displayName: name,
    createdBy: userId,
    userId,
  });
  await patchUserProfile(userId, { playerId: player.playerId });
  trackProfileCreated();
  return player;
}
